/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 16-09-2026
 *
 * De aansturing van de simulatie: starten, stoppen, een stap zetten, het snelste pad omwisselen en de
 * toestanden mooi neerleggen — plus het wachten dat daarbij hoort.
 *
 * Sinds golf 3.6 rekent het backend buiten de request-lijn. `simulate.start` betekent daar "zorg dat
 * de simulatie van dit model er is" en antwoordt meteen: `klaar`, of `gestart`/`bezig` als er nog
 * gerekend wordt — altijd met de invoerhash. De simulatie zelf komt sinds 17-09-2026 alleen via
 * `simulate.ophalen`, op model en hash, en dat rekent nooit. Deze service haalt op zodra er iets te
 * halen valt: meteen bij `klaar`, en anders op de notify `simulatieklaar`, bij een herregistratie of
 * op een goedkope poll. Zo kan een hervraag nooit per ongeluk een nieuwe berekening starten met een
 * intussen gewijzigd model, en is er niets dat een client na een verbroken verbinding hoeft terug te
 * vinden.
 *
 * Tijdens het wachten staat bij iedereen op het model de simview open met een spinner erover
 * (testronde 16-09-2026): bij de uitvoerder en bij zijn volgers hetzelfde beeld. Het modelcanvas blijft
 * bedienbaar; een inhoudelijke wijziging stopt de simulatie, en dat wordt gemeld.
 *
 * Wat hier **niet** in zit: het simcanvas zelf (dat is het eiland), het simulatiebericht naar de
 * volgers (dat is de samenwerk-baas van a4) en de windowsplit (de `SchermindelingService`, toegepast
 * door het bouwscherm).
 */

import {inject, Injectable, signal} from '@angular/core';
import {Sihwlog} from 'sihw-ngx';
import {ApiService} from '../backend/api.service';
import {OpslagService} from '../model/opslag.service';
import {SamenwerkprotocolService} from '../samenwerking/samenwerkprotocol.service';
import {SimulatieStatusService} from '../../services/simulatie-status.service';
import {SamenwerkingService} from '../../services/samenwerking.service';
import {DlModel} from '../model/dl-model';
import {ToastService} from '../../services/toast.service';
import {VideoStatusService} from '../../services/video-status.service';
import {SimfeedbackEngineService} from '../engines/simfeedback-engine.service';
import {FeedbackSelectieService} from '../../services/feedback-selectie.service';
import {GlobalSimcanvasService} from './global-simcanvas.service';
import type {SimcanvasEiland} from './global-simcanvas.service';
import type {SimulatieData} from '../../components/canvas/dl-simcanvas/dl-simcanvas.component';
import {Melding} from '../../lib/melding';

//De `SimviewUi` (de ng1-plumbing rond de simview) is met golf 4e vervallen: het openen en sluiten van
//het scherm is `SimulatieStatusService.zetSimuleren`, het tekenen gaat naar het simcanvas-eiland uit de
//`GlobalSimcanvasService`, en dat de tekstbeschrijving weet dat er gesimuleerd is, is een melding.

/**
 * Hoe lang we wachten met de toast. Een simulatie uit de cache is er vrijwel meteen, en dan zou de
 * melding alleen maar flitsen. De spinner in de simview staat er wél meteen.
 */
const TOAST_NA_MS = 500;

/** Hoe vaak we navragen of er nog gerekend wordt, zolang we wachten. */
const POLL_MS = 7000;

/**
 * Hoe vaak we hoogstens opnieuw vragen zonder ooit een simulatie te krijgen. Het laatste vangnet tegen
 * een eindeloze lus; de normale afloop is de notify of de poll die zegt dat er niets meer rekent.
 */
const MAX_POGINGEN = 200;

@Injectable({providedIn: 'root'})
export class SimulatieService {

    private readonly log = new Sihwlog('info', 'SimulatieService');
    private readonly api = inject(ApiService);
    private readonly opslag = inject(OpslagService);
    private readonly samenwerkprotocol = inject(SamenwerkprotocolService);
    private readonly simulatieStatus = inject(SimulatieStatusService);
    private readonly samenwerking = inject(SamenwerkingService);
    private readonly dlModel = inject(DlModel);
    private readonly toast = inject(ToastService);
    private readonly videoStatus = inject(VideoStatusService);

    /** De simfeedback: rekent zodra een simulatie getekend wordt. */
    private readonly simfeedbackEngine = inject(SimfeedbackEngineService);

    /** De aanwijzing op het modelcanvas: die hoort bij de vorige stand en gaat weg bij een simulatie. */
    private readonly feedbackSelectie = inject(FeedbackSelectieService);

    /** Het simulatiecanvas, dat de simulatie tekent. `null` zolang er geen simulatiescherm is. */
    private readonly globalSimcanvas = inject(GlobalSimcanvasService);

    /**
     * Meldt dat er een simulatie getekend is. De tekstbeschrijving zet daarop zijn vetgedrukte regels
     * terug; tot golf 4e liep dat langs de `SimviewUi`.
     */
    private readonly gesimuleerdMelding = new Melding<void>();

    /** Of we op een simulatie wachten. */
    private readonly _wacht = signal(false);

    /** Sinds wanneer er gerekend wordt (epoch in ms), voor "al vier minuten bezig". */
    private readonly _wachtSinds = signal(0);

    /** Het model waarop we wachten, of `null` als we nergens op wachten. */
    private wachtOpModel: string | null = null;

    /**
     * Het model waarop het **backend** deze simulatie sleutelt: bij een sub het primaire model, anders
     * het model zelf. Dat is iets anders dan `wachtOpModel`, dat het model is waarin wíj werken.
     *
     * De runner zet zijn taak en zijn memcache-regel op het primary, en zijn notify draagt datzelfde
     * id. Twee acties van het backend vertalen een sub-id zelf naar het primary (`simulate.start` en
     * `simulate.ophalen` doen `leesModel`), maar `simulate.bezig` niet: die leest de memcache
     * rechtstreeks op wat de client meestuurt. Wie daar zijn eigen sub-id heen stuurt krijgt dus altijd
     * `bezig: false`, ook al staat de runner te rekenen — en dan haalt de poll op terwijl er nog niets
     * ligt, krijgt `NOT_FOUND` en breekt de simulatie af. Dat is geen race maar vaste prik voor elk
     * submodel; gevonden 23-09-2026 bij test 21 van golf 4e, met een leider die de sub open had.
     */
    private get simulatiesleutel(): string | null {
        return this.opslag.notifyId() ?? this.wachtOpModel;
    }

    /** De invoerhash waarop we wachten, uit het antwoord van `start`; `null` zolang dat nog onderweg is. */
    private wachtHash: string | null = null;

    /** Of er een `ophalen` onderweg is. Een tweede zou dezelfde (mogelijk tientallen MB grote) simulatie nog eens halen. */
    private ophalenOnderweg = false;

    /** Of de simulatie die we wachten een volledige is (anders stap voor stap). */
    private wachtVol = false;

    /** Of de laatste simulatie een volledige was; bepaalt wat een herstart doet. */
    private laatsteVol = false;

    /** De poll-timer, zolang het backend rekent en er geen vraag onderweg is. */
    private pollTimer: ReturnType<typeof setInterval> | null = null;

    /** De timer die de toast na een korte drempel laat zien. */
    private toastTimer: ReturnType<typeof setTimeout> | null = null;

    /** Hoe vaak we voor dit wachten al opnieuw gevraagd hebben. */
    private pogingen = 0;

    /** Of wij als volger op de simulatie van de uitvoerder wachten; zie `volgerBezig`. */
    private volgerWacht = false;

    /**
     * Of de melding "er wordt een simulatie gegenereerd" getoond is voor dit wachten. Zo ja, dan hoort
     * er altijd een vervolg bij: de simulatie, of de melding dat het gestopt is.
     */
    private genererenGemeld = false;

    /** Of we wachten op een simulatie. */
    public readonly wacht = this._wacht.asReadonly();

    /** Sinds wanneer er gerekend wordt, als epoch in ms; 0 als er niets loopt. */
    public readonly wachtSinds = this._wachtSinds.asReadonly();

    constructor() {
        //"er valt iets te halen voor dit model". De melding draagt geen data en gaat naar iedereen op
        //het model: wie er niet op wacht, doet er niets mee
        this.api.onNotify<{model: string}>('simulatieklaar')
            .subscribe(data => this.bijSimulatieklaar(data?.model));
        //na een herstelde verbinding is de notify van tijdens de onderbreking verloren. Wie nog wacht,
        //vraagt het daarom gewoon opnieuw
        this.api.onNotify('registratieVerzoek').subscribe(() => this.hervraagIndienNodig());
        //de vier ingangen van de simulatieknoppen: de baas bepaalt wie de uitvoerder is en roept ze aan.
        //Een registratie en geen injectie, want de baas kan deze service niet injecteren (cyclus). Tot
        //golf 4e deed de AngularJS-hoofdcontroller deze registratie
        this.samenwerkprotocol.registreerSimulatieAansturing({
            startOfStop: volger => this.startOfStop(volger),
            volgendeStap: volger => this.volgendeStap(volger),
            wisselSnelstePad: () => this.wisselSnelstePad(),
            mooieStates: volger => void this.mooieStates(volger),
            bezigVanLeider: (bezig, gestopt) => this.volgerBezig(bezig, gestopt)
        });
        //een wijziging van de inhoud of de eigenschappen sluit de simulatie: die hoort bij het model
        //zoals het wás. Synchroon, zodat de opslagronde van de baas — die de meldronde afwacht — hem
        //daarna dicht ziet en zijn bouwsuggestie-run wél doet (golf 4d keuze 4)
        this.opslag.bijInhoudGewijzigd(() => this.sluitBijWijziging());
        this.opslag.bijPropsGewijzigd(() => this.sluitBijWijziging());
        //en een startende instructievideo net zo goed
        this.videoStatus.bijSpeeltGewijzigd(speelt => {
            if (speelt) {
                this.sluitBijWijziging();
            }
        });
    }

    /**
     * Sluit de simulatie omdat er iets veranderde waar hij niet meer bij past. Staat hij niet open, dan
     * is er niets te doen — zoals `toggleSimulationMode` in AngularJS bij een gelijke stand terugkeerde.
     */
    private sluitBijWijziging(): void {
        if (this.simulatieStatus.simuleren()) {
            this.stop();
        }
    }

    /**
     * Het simulatiecanvas, of `null` als er (nog) geen simulatiescherm is.
     */
    private get simcanvas(): SimcanvasEiland | null {
        return this.globalSimcanvas.getEiland();
    }

    /**
     * Abonneert op "er is een simulatie getekend"; geeft de afmeldfunctie terug.
     */
    public bijGesimuleerd(luisteraar: () => void): () => void {
        return this.gesimuleerdMelding.abonneer(luisteraar);
    }

    /* ***************** DE VIER INGANGEN ***************** */

    /**
     * Start de simulatie, of stop hem als hij al draait. De ingang van de simulatieknop; de
     * samenwerk-baas roept hem aan nadat hij bepaald heeft dat wij de uitvoerder zijn.
     * @param [volger] het clientid van de volger die dit vroeg, voor het actielog
     */
    public startOfStop(volger?: string | null): void {
        if (this.simulatieStatus.simuleren()) {
            this.opslag.directlogAction('stop_sim', {}, 'model', 'model', false, volger ?? null);
            this.stop(false, true);
            //de snapshot volgt als het model tot rust gekomen is, dus zónder simulatie
            this.opslag.markeerSnapshotNodig();
            return;
        }
        this.opslag.directlogAction('start_sim', {fullsim: true}, 'model', 'model', false, volger ?? null);
        void this.start(true);
    }

    /**
     * Zet de volgende stap in de simulatie, of start hem stap voor stap als hij nog dicht is.
     * @param [volger] het clientid van de volger die dit vroeg, voor het actielog
     */
    public volgendeStap(volger?: string | null): void {
        if (!this.simulatieStatus.simuleren()) {
            this.opslag.directlogAction('start_sim', {fullsim: false}, 'model', 'model', false, volger ?? null);
            void this.start(false);
            return;
        }
        this.opslag.directlogAction('sim_nextstep', {}, 'model', 'model', false, volger ?? null);
        this.opslag.markeerSnapshotNodig();
        this.simcanvas?.nextStep();
    }

    /**
     * Wisselt het snelste pad om en start de simulatie opnieuw. De vlag gaat bewust niet via
     * `setProperties`: hij is puur weergave.
     */
    public wisselSnelstePad(): void {
        this.stop(false, true);
        this.dlModel.fastest_path = !this.dlModel.fastest_path;
        this.samenwerkprotocol.stuurSim('SetFastestPath', {fastest_path: this.dlModel.fastest_path});
        void this.start(this.laatsteVol, true);
    }

    /**
     * Legt de toestanden mooi neer.
     * @param [volger] het clientid van de volger die dit vroeg, voor het actielog
     */
    public async mooieStates(volger?: string | null): Promise<void> {
        this.opslag.directlogAction('nice_states', {}, 'model', 'model', false, volger ?? null);
        this.opslag.markeerSnapshotNodig();
        //het simcanvas gaat even op slot terwijl de layout neergelegd wordt. Dat is hetzelfde slot dat
        //de replay gebruikt; de jQuery-blocker die hier tot golf 3.6 voor stond is vervallen
        this.samenwerkprotocol.zetSimviewReadonly(true);
        try {
            await this.simcanvas?.niceStates();
            //de nieuwe lay-out gaat naar de andere deelnemers, met de vraag hun beeld bij te trekken;
            //tot golf 4e stond die regel in de ng1-wrapper om `niceStates`
            this.simcanvas?.planStatusVersturen(false, true);
        } finally {
            this.samenwerkprotocol.zetSimviewReadonly(false);
        }
    }

    /* ***************** DE FLOW ***************** */

    /**
     * Vraagt de simulatie op en tekent hem, of gaat wachten als het backend nog aan het rekenen is.
     *
     * De volgorde is niet vrijblijvend: eerst opslaan (er wordt gerekend op de stand die er net in
     * ging), dan de wachtvlag zetten, en pas dán vragen. Zou de vlag ná de call komen, dan kan de
     * notify van een snel model aankomen terwijl we nog op het antwoord wachten, en negeren we hem.
     * @param fullsim of de simulatie meteen helemaal getekend wordt
     * @param [force] reken opnieuw, ook als er een geldige cache is
     */
    public async start(fullsim: boolean, force?: boolean): Promise<void> {
        const modelid = this.opslag.modelId();
        if (!modelid) {
            return;
        }
        this.videoStatus.cancel(); //een lopende instructievideo wijkt voor de simulatie
        this.simulatieStatus.zetSimuleren(true);
        this.gesimuleerdMelding.meld();
        this.laatsteVol = fullsim;
        this.beginWachten(modelid, fullsim);
        //eerst opslaan: er wordt gerekend op de stand die er net in ging (bestaand gedrag)
        await this.opslag.saveModel();
        await this.vraagSimulatie(force);
    }

    /**
     * Stopt de simulatie: het scherm dicht, het canvas leeg, en niet meer wachten. Werd er nog op een
     * simulatie gewacht, dan volgt de melding dat het gestopt is: altijd als het buiten de gebruiker om
     * ging (een inhoudelijke wijziging, een ander model, een mislukking), en bij zijn eigen stop alleen
     * als hij de melding "wordt gegenereerd" al gezien had.
     * @param [skipSamenwerking] bij het uitzetten geen simulatiebericht naar de leider/volgers
     * @param [eigenGebaar] de gebruiker stopte zelf
     */
    public stop(skipSamenwerking?: boolean, eigenGebaar = false): void {
        this.stopWachten(eigenGebaar ? 'alsGemeld' : 'altijd');
        this.simulatieStatus.zetSimuleren(false);
        //`reset` en niet `ruimSimulatieOp`: de eerste bewaart de posities voor een identieke herstart en
        //stuurt de nieuwe (lege) stand rond, de tweede wist alleen
        this.simcanvas?.reset(skipSamenwerking);
        //de simfeedback hoort bij de simulatie die net dicht ging: data en aanwijzing weg, de toon-vlag
        //blijft (documentstate). Tot golf 4d deed de AngularJS-modelview dit na het opruimen
        this.samenwerkprotocol.pasSimfeedbackStatusToe(null);
    }

    /**
     * Doet het `start`-verzoek en handelt de antwoorden af. Elk antwoord behalve een fout draagt de
     * invoerhash: die wordt de hash waarop we wachten.
     * @param [force] reken opnieuw, ook als er een geldige cache is
     */
    private async vraagSimulatie(force?: boolean): Promise<void> {
        const modelid = this.wachtOpModel;
        if (!modelid) {
            return;
        }
        //zolang deze vraag onderweg is, pollen we niet
        this.stopPoll();
        const uitkomst = await this.api.simulate(modelid, force, null);
        if (this.wachtOpModel !== modelid) {
            return; //we wachten hier niet meer op; wat er binnenkomt is niet meer van ons
        }
        if (uitkomst.resultaat === false || !uitkomst.invoerhash) {
            this.stop();
            return;
        }
        this.wachtHash = uitkomst.invoerhash;
        switch (uitkomst.status) {
            case 'klaar':
                await this.haalOp();
                break;
            case 'gestart':
            case 'bezig':
                //er wordt gerekend; we horen het via de notify, en halen anders op als de poll zegt dat
                //er niets meer rekent
                this._wachtSinds.set(uitkomst.sinds || Date.now());
                this.startPoll();
                break;
            default:
                this.stop();
                break;
        }
    }

    /**
     * Haalt de simulatie op waarop we wachten, en tekent hem. Rekent nooit: staat hij er niet — de
     * berekening mislukte, of hij is intussen opgeruimd — dan stoppen we, met de melding dat het
     * gestopt is.
     */
    private async haalOp(): Promise<void> {
        const modelid = this.wachtOpModel;
        const hash = this.wachtHash;
        if (!modelid || !hash || this.ophalenOnderweg) {
            return;
        }
        if (++this.pogingen > MAX_POGINGEN) {
            this.log.warn('te vaak om dezelfde simulatie gevraagd; we stoppen ermee');
            this.stop();
            return;
        }
        this.stopPoll();
        this.ophalenOnderweg = true;
        let simulatie: unknown | null;
        try {
            simulatie = await this.api.simulatieOphalen(modelid, hash);
        } finally {
            this.ophalenOnderweg = false;
        }
        if (this.wachtOpModel !== modelid || this.wachtHash !== hash) {
            return; //we wachten hier niet meer op
        }
        if (!simulatie) {
            this.stop();
            return;
        }
        this.pasToe(simulatie, hash);
    }

    /**
     * Tekent een binnengekomen simulatie — maar alleen als de simview nog open staat en we geen volger
     * zijn geworden. Dat vangt het geval af dat de stand veranderde tussen onze vraag en het antwoord.
     *
     * "Mag uitvoeren" is `werkmodus !== 'volg'` en niet `=== 'leid'`: er zijn drie modi, en `lokaal` —
     * de gewone solo-stand — voert net zo goed zelf uit.
     * @param simulatie de simulatiedata van het backend
     * @param invoerhash de hash die erbij hoort
     */
    private pasToe(simulatie: unknown, invoerhash: string | null): void {
        const fullsim = this.wachtVol;
        if (!this.simulatieStatus.simuleren() || this.samenwerking.isVolger()) {
            this.stopWachten('altijd'); //de rol of het scherm veranderde buiten ons gebaar om
            return;
        }
        this.stopWachten();
        //een aanwijzing (een fout, een suggestie, een lus) wijst naar de stand van vóór deze simulatie:
        //die gaat weg, zoals hij ook weggaat als de simulatie stopt. Een volger deed dit al, omdat de
        //binnenkomende simulatiestatus langs `pasSimfeedbackStatusToe` gaat (testronde 4d, test 16)
        this.feedbackSelectie.wisSelectie();
        //de simfeedback rekent op het modelcanvas en niet op de simulatie zelf, dus vóór het tekenen, zoals
        //in AngularJS (daar deed de modelview het). Een volger rekent niet: die komt hier niet
        this.simfeedbackEngine.run();
        this.simcanvas?.startSimulatie(simulatie as SimulatieData, fullsim, invoerhash);
        //de simulatie stáát nu; dat is een toestandswijziging als elke andere, dus vraagt om een
        //screenshot van deze rusttoestand (kernbesluit 10). Mislukt de simulatie of komt hij niet meer
        //van pas, dan komt er geen vlag en dus ook geen stap in de replay — precies goed
        this.opslag.markeerSnapshotNodig();
    }

    /* ***************** HET WACHTEN ***************** */

    /**
     * Zet de wachtstand op: de spinner in de simview (meteen), de toast (na een korte drempel) en de
     * melding naar de volgers. De poll hoort hier niet bij: die begint pas als het backend antwoordt dat
     * er gerekend wordt (zie `vraagSimulatie`).
     * @param modelid het model waarop we wachten
     * @param fullsim of we een volledige simulatie verwachten
     */
    private beginWachten(modelid: string, fullsim: boolean): void {
        this.stopWachten();
        this.wachtOpModel = modelid;
        this.wachtHash = null;
        this.wachtVol = fullsim;
        this.pogingen = 0;
        this._wacht.set(true);
        this._wachtSinds.set(Date.now());
        this.simulatieStatus.zetGenereren(true);
        this.samenwerkprotocol.stuurSim('simbezig', {bezig: true});
        this.planToast();
    }

    /**
     * Ruimt de wachtstand op: spinner weg, timers weg, en de volgers horen dat het voorbij is — met de
     * vermelding of het gestopt is, zodat zij dezelfde melding kunnen tonen.
     * @param [melding] `geen`: de simulatie kwam (of er begint een nieuw wachten); de melding "wordt
     *   gegenereerd" verdwijnt dan. `altijd`: gestopt buiten de gebruiker om, dus altijd melden.
     *   `alsGemeld`: gestopt door de gebruiker zelf, dus alleen melden als hij "wordt gegenereerd" zag
     */
    private stopWachten(melding: 'geen' | 'altijd' | 'alsGemeld' = 'geen'): void {
        this.wisToastTimer();
        this.stopPoll();
        if (this.wachtOpModel) {
            const gestopt = melding !== 'geen';
            this.samenwerkprotocol.stuurSim('simbezig', {bezig: false, gestopt});
            this.meldAfloop(gestopt, melding === 'altijd');
        }
        this.wachtOpModel = null;
        this.wachtHash = null;
        this._wacht.set(false);
        this._wachtSinds.set(0);
        this.simulatieStatus.zetGenereren(false);
    }

    /**
     * Zet de toast "er wordt een simulatie gemaakt" klaar voor als het langer duurt dan een flits.
     */
    private planToast(): void {
        this.wisToastTimer();
        this.genererenGemeld = false;
        this.toastTimer = setTimeout(() => {
            this.toastTimer = null;
            if (this._wacht() || this.volgerWacht) {
                this.toast.toon('SIMULATIE.GENEREREN');
                this.genererenGemeld = true;
            }
        }, TOAST_NA_MS);
    }

    /**
     * De melding bij het einde van een wachten. Kwam de simulatie, dan verdwijnt "wordt gegenereerd"
     * gewoon; kwam hij niet, dan volgt "gestopt" — altijd als dat zo afgesproken is, en anders alleen
     * als de gebruiker "wordt gegenereerd" gezien had, zodat die melding nooit zonder vervolg blijft.
     * @param gestopt het wachten eindigde zonder simulatie
     * @param altijdMelden ook melden als "wordt gegenereerd" niet getoond is
     */
    private meldAfloop(gestopt: boolean, altijdMelden: boolean): void {
        if (gestopt && (altijdMelden || this.genererenGemeld)) {
            this.toast.toon('SIMULATIE.GESTOPT', 'waarschuwing');
        } else if (this.genererenGemeld && this.toast.toast()?.sleutel === 'SIMULATIE.GENEREREN') {
            this.toast.verberg();
        }
        this.genererenGemeld = false;
    }

    /**
     * Haalt een klaargezette toast weg.
     */
    private wisToastTimer(): void {
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
            this.toastTimer = null;
        }
    }

    /**
     * Zet de poll aan, als hij nog niet loopt.
     */
    private startPoll(): void {
        this.pollTimer ??= setInterval(() => void this.pollBezig(), POLL_MS);
    }

    /**
     * Zet de poll uit.
     */
    private stopPoll(): void {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    /**
     * De goedkope navraag terwijl er gerekend wordt: rekent er nog iets voor dit model? Zo niet, dan is
     * er iets afgelopen zonder dat we de notify zagen — bijvoorbeeld omdat de runner crashte — en halen
     * we op. Staat er dan niets, dan was het een mislukking. Eén memcache-read aan de andere kant, dus
     * dit mag elke paar seconden.
     */
    private async pollBezig(): Promise<void> {
        const modelid = this.wachtOpModel;
        if (!modelid) {
            return;
        }
        //met de sleutel van het backend, niet met ons eigen model-id: zie `simulatiesleutel`
        const stand = await this.api.simulatieBezig(this.simulatiesleutel ?? modelid);
        //ging er intussen een vraag weg (de notify, een herregistratie), dan staat de poll uit en is dit
        //antwoord achterhaald
        if (this.wachtOpModel !== modelid || !this.pollTimer) {
            return;
        }
        if (stand.bezig) {
            this._wachtSinds.set(stand.sinds || this._wachtSinds());
            return;
        }
        await this.haalOp();
    }

    /**
     * Het backend meldt dat er voor dit model iets te halen valt. De melding draagt geen data en mag
     * zonder gevolgen gemist worden; wie er niet op wacht, doet er niets mee.
     * @param modelid het model uit de notify
     */
    public bijSimulatieklaar(modelid: string): void {
        //de notify draagt het id waarop de runner sleutelt — bij een sub dus het primary, en niet het
        //model waarin wij werken. Vergelijken met `wachtOpModel` liet een sub zijn eigen melding missen
        if (this.wachtOpModel && this.simulatiesleutel === modelid) {
            void this.haalOp();
            return;
        }
        //een volger die op de leider wachtte hoort hier op te houden met wachten: de uitvoerder stuurt
        //zijn `simbezig: false` niet meer als hij intussen wegviel of zijn leiding kwijtraakte
        if (this.volgerWacht) {
            this.volgerBezig(false);
        }
    }

    /**
     * Neemt over dat de uitvoerder op een simulatie wacht, voor een volger. Die krijgt hetzelfde beeld
     * als de uitvoerder: de simview meteen open met de spinner erover, de toast als het langer duurt, en
     * een melding als het gestopt is.
     * @param bezig of de uitvoerder aan het wachten is
     * @param [gestopt] het wachten eindigde zonder simulatie; dan volgt "gestopt" als de volger
     *   "wordt gegenereerd" gezien had
     */
    public volgerBezig(bezig: boolean, gestopt = false): void {
        if (bezig === this.volgerWacht) {
            return;
        }
        this.volgerWacht = bezig;
        this.simulatieStatus.zetGenereren(bezig);
        if (bezig) {
            this.simulatieStatus.zetSimuleren(true);
            this.planToast();
            return;
        }
        this.wisToastTimer();
        this.meldAfloop(gestopt, false);
    }

    /**
     * De invoerhash van dit model zoals het nu is. Haalt geen simulatie op en start niets: het backend
     * rekent alleen de prolog uit.
     *
     * De replay gebruikt dit bij het terugkeren: is de hash dezelfde als die van de simulatie die je
     * bij het binnengaan had staan, dan hoort die simulatie nog bij dit model en mag hij terug.
     */
    public async geldigeInvoerhash(): Promise<string | null> {
        const modelid = this.opslag.modelId();
        return modelid ? this.api.simulatieInvoerhash(modelid) : null;
    }

    /**
     * Na een herregistratie (een herstelde verbinding, of terugkeer uit de replay) ophalen als we nog op
     * een simulatie wachtten. Anders blijft de blokkade staan terwijl er niets meer komt: de notify van
     * tijdens de onderbreking is verloren. Rekent het backend nog, dan is hij er niet en stoppen we —
     * daarom eerst navragen, en alleen ophalen als er niets meer rekent.
     */
    public hervraagIndienNodig(): void {
        if (this.wachtOpModel && this.wachtHash) {
            this.startPoll();
            void this.pollBezig();
        }
    }
}
